// components/unit/Lightbox.tsx
/**
 * @mockup /design/mockups/04-mobile-photo-gallery.png
 * @description Full-screen photo gallery with thumbnail navigation
 */
import { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export interface Photo {
  label: string;
  url: string;
}

interface LightboxProps {
  photos: Photo[];
  initialIndex: number;
  onClose: () => void;
}

export function Lightbox({ photos, initialIndex, onClose }: LightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // Prevent scrolling on body when gallery is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  };

  const handlePrev = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  const currentPhoto = photos[currentIndex];

  return (
    <div className="fixed inset-0 z-[100] bg-[#111827]/95 backdrop-blur-sm flex flex-col text-white">
      {/* Top Bar */}
      <div className="flex items-center justify-between p-4 h-16 z-10">
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors focus:outline-none active:bg-white/30"
          aria-label="Close gallery"
        >
          <X size={24} />
        </button>
        <span className="font-medium text-sm tracking-wide">
          {currentIndex + 1} of {photos.length}
        </span>
        <div className="w-10" /> {/* Spacer for centering balance */}
      </div>

      {/* Main Content */}
      <div
        className="flex-1 relative flex items-center justify-center p-2"
        onClick={onClose}
      >
        {/* Navigation Buttons */}
        <button
          onClick={handlePrev}
          className="absolute left-2 p-4 text-white/75 hover:text-white transition-colors z-20 focus:outline-none"
          aria-label="Previous photo"
        >
          <ChevronLeft size={36} />
        </button>

        <img
          src={currentPhoto.url}
          alt={currentPhoto.label}
          className="max-h-full max-w-full object-contain shadow-2xl transition-opacity duration-300"
          onClick={(e) => e.stopPropagation()}
        />

        <button
          onClick={handleNext}
          className="absolute right-2 p-4 text-white/75 hover:text-white transition-colors z-20 focus:outline-none"
          aria-label="Next photo"
        >
          <ChevronRight size={36} />
        </button>
      </div>

      {/* Footer Info & Thumbnails */}
      <div className="pb-8 pt-4 bg-gradient-to-t from-black/50 to-transparent">
        <p className="text-center text-lg font-medium mb-6">
          {currentPhoto.label}
        </p>

        {/* Thumbnail Strip */}
        <div className="flex gap-3 px-4 overflow-x-auto no-scrollbar justify-start md:justify-center">
          {photos.map((photo, index) => (
            <button
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                setCurrentIndex(index);
              }}
              className={`relative flex-shrink-0 w-14 h-14 rounded-md overflow-hidden transition-all duration-200 ${
                index === currentIndex
                  ? 'ring-2 ring-white ring-offset-2 ring-offset-[#111827] opacity-100 scale-105'
                  : 'opacity-50 hover:opacity-75'
              }`}
            >
              <img
                src={photo.url}
                alt={photo.label}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
